import cx from 'classnames'
import React, { FC, useMemo } from 'react'
import { motionGroup, separatorReveal, slideReveal } from 'src/hooks/useAppear'

import { ActionButtonProps } from '../ActionButton'
import { DownloadButtonProps } from '../DownloadButton'
import { RichTextProps } from '../RichText'

import * as SC from './styled'

export type EditoProps = MinimalSeoBlock & {
  className?: string
  title?: string
  richTextProps?: RichTextProps
  media?: React.ReactNode
  mediaFirstOnMobile?: boolean // default is true
  mediaFirstOnDesktop?: boolean // default is true
  actionButtons?: (ActionButtonProps | undefined)[]
  downloadButtons?: (DownloadButtonProps | undefined)[]
  color?: string
  compact?: boolean // no border on desktop, less space above
}

const Edito: FC<EditoProps> = ({
  className,
  title,
  richTextProps,
  media,
  mediaFirstOnMobile = true,
  mediaFirstOnDesktop = true,
  actionButtons,
  downloadButtons,
  htmlTag,
  htmlTitleTag,
  compact = false,
}) => {
  const { motions, variants } = useEditoMotion()

  return (
    <SC.Container
      className={cx('Edito', className)}
      as={htmlTag}
      $mediaFirstOnMobile={mediaFirstOnMobile}
      $mediaFirstOnDesktop={mediaFirstOnDesktop}
      $compact={compact}
      {...motions.container}
    >
      <SC.Separator
        role="separator"
        aria-hidden
        $visible={!compact}
        variants={variants.separator}
      />
      {media && (
        <SC.Media variants={variants.content(mediaFirstOnDesktop ? 0.2 : 0.4)}>
          {media}
        </SC.Media>
      )}
      {(title || richTextProps) && (
        <SC.Content
          variants={variants.content(mediaFirstOnDesktop ? 0.4 : 0.2)}
          $noMedia={media ? false : true}
        >
          {title && <SC.Title as={htmlTitleTag}>{title}</SC.Title>}
          {richTextProps && <SC._RichText {...richTextProps} />}
          {actionButtons && actionButtons.length > 0 && (
            <SC.Buttons>
              {actionButtons.map((actionButton, index) =>
                actionButton ? (
                  <SC._ActionButton key={index} {...actionButton} />
                ) : null
              )}
            </SC.Buttons>
          )}
          {downloadButtons && downloadButtons.length > 0 && (
            <SC.Buttons>
              {downloadButtons.map((downloadButton, index) =>
                downloadButton ? (
                  <SC._DownloadButton key={index} {...downloadButton} />
                ) : null
              )}
            </SC.Buttons>
          )}
        </SC.Content>
      )}
    </SC.Container>
  )
}

const useEditoMotion = () => {
  const motions = useMemo(
    () => ({
      container: motionGroup({}).motionProps,
    }),
    []
  )

  const variants = useMemo(
    () => ({
      separator: separatorReveal({}),
      content: (delay: number) => slideReveal({ delay }),
    }),
    []
  )

  return { motions, variants }
}

export default Edito
